import React, { useState, useEffect } from 'react';
import { zonesApi, parcelsApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Layers, Plus, MoreHorizontal, Trash2, Loader2, Droplets, Thermometer, Cloud, Warehouse } from 'lucide-react';

const ZonesPage = () => {
  const [zones, setZones] = useState([]);
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    zone_type: '',
    parcel_id: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const { isAdmin } = useAuth();

  const fetchData = async () => {
    try {
      const [zonesRes, parcelsRes] = await Promise.all([
        zonesApi.getAll(),
        parcelsApi.getAll(),
      ]);
      setZones(zonesRes.data);
      setParcels(parcelsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = () => {
    setFormData({
      name: '',
      description: '',
      zone_type: 'monitoring',
      parcel_id: parcels[0]?.id || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await zonesApi.create({
        name: formData.name,
        description: formData.description || null,
        zone_type: formData.zone_type || null,
        parcel_id: formData.parcel_id,
      });
      toast.success('Zone créée');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette zone ?')) return;

    try {
      await zonesApi.delete(id);
      toast.success('Zone supprimée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const getParcelName = (parcelId) => {
    const parcel = parcels.find((p) => p.id === parcelId);
    return parcel?.name || 'Inconnu';
  };

  const zoneTypeConfig = {
    irrigation: { label: 'Irrigation', icon: Droplets, color: 'bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400' },
    monitoring: { label: 'Surveillance', icon: Thermometer, color: 'bg-amber-100 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400' },
    weather: { label: 'Météo', icon: Cloud, color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
    greenhouse: { label: 'Serre', icon: Warehouse, color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400' },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="zones-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Zones</h1>
          <p className="text-muted-foreground mt-1">Gérez les zones de vos parcelles</p>
        </div>
        {isAdmin && parcels.length > 0 && (
          <Button onClick={handleOpenDialog} data-testid="add-zone-btn">
            <Plus className="mr-2 h-4 w-4" />
            Nouvelle zone
          </Button>
        )}
      </div>

      {/* Cards Grid */}
      {zones.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Layers className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Aucune zone</p>
            {parcels.length === 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                Créez d'abord une parcelle
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {zones.map((zone) => {
            const typeConfig = zoneTypeConfig[zone.zone_type] || zoneTypeConfig.monitoring;
            const TypeIcon = typeConfig.icon;

            return (
              <Card key={zone.id} className="card-hover" data-testid={`zone-card-${zone.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${typeConfig.color}`}>
                        <TypeIcon className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{zone.name}</CardTitle>
                        <CardDescription className="text-sm mt-0.5">
                          {getParcelName(zone.parcel_id)}
                        </CardDescription>
                      </div>
                    </div>
                    {isAdmin && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`zone-menu-${zone.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => handleDelete(zone.id)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {zone.description && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {zone.description}
                    </p>
                  )}
                  <Badge className={typeConfig.color}>
                    {typeConfig.label}
                  </Badge>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle zone</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nom *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="zone-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="parcel_id">Parcelle *</Label>
                <Select
                  value={formData.parcel_id}
                  onValueChange={(value) => setFormData({ ...formData, parcel_id: value })}
                >
                  <SelectTrigger data-testid="zone-parcel-select">
                    <SelectValue placeholder="Sélectionner une parcelle" />
                  </SelectTrigger>
                  <SelectContent>
                    {parcels.map((parcel) => (
                      <SelectItem key={parcel.id} value={parcel.id}>
                        {parcel.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="zone_type">Type de zone</Label>
                <Select
                  value={formData.zone_type}
                  onValueChange={(value) => setFormData({ ...formData, zone_type: value })}
                >
                  <SelectTrigger data-testid="zone-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="irrigation">Irrigation</SelectItem>
                    <SelectItem value="monitoring">Surveillance</SelectItem>
                    <SelectItem value="weather">Météo</SelectItem>
                    <SelectItem value="greenhouse">Serre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  data-testid="zone-description-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={submitting} data-testid="zone-submit-btn">
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Créer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ZonesPage;
